import { cn } from '@cotera/client/v0/lib/utils';
import { type ComponentProps, memo } from 'react';
import { Streamdown } from 'streamdown';
import {
  RESPONSE_MARKDOWN_COMPONENTS,
  RESPONSE_MERMAID_OPTIONS,
  RESPONSE_REHYPE_PLUGINS,
  RESPONSE_REMARK_PLUGINS,
} from './response-markdown';

type ResponseProps = ComponentProps<typeof Streamdown>;

export const Response = memo(
  ({
    className,
    components,
    rehypePlugins,
    remarkPlugins,
    mermaid,
    ...props
  }: ResponseProps) => (
    <Streamdown
      className={cn(
        'size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_ol]:list-inside [&_ul]:list-inside',
        className
      )}
      components={components ?? RESPONSE_MARKDOWN_COMPONENTS}
      rehypePlugins={rehypePlugins ?? RESPONSE_REHYPE_PLUGINS}
      remarkPlugins={remarkPlugins ?? RESPONSE_REMARK_PLUGINS}
      mermaid={mermaid ?? RESPONSE_MERMAID_OPTIONS}
      {...props}
    />
  ),
  // Compares every prop this component actually forwards. `components` and
  // `rehypePlugins` are module-level constants (see ./response-markdown), so
  // reference equality holds and Streamdown's per-block memoization is
  // preserved. Note this comparator is only a guard: Streamdown's own memo
  // ignores `components` entirely, which is why citation data reaches the
  // renderer through context rather than through props.
  (prevProps, nextProps) =>
    prevProps.children === nextProps.children &&
    prevProps.className === nextProps.className &&
    prevProps.components === nextProps.components &&
    prevProps.rehypePlugins === nextProps.rehypePlugins &&
    prevProps.remarkPlugins === nextProps.remarkPlugins
);

Response.displayName = 'Response';
